import { Box, Grid, IconButton, Typography } from '@material-ui/core'
import { DownloadIcon } from 'asterX-UI/Icons/DownloadIcon'
import { FileIcon } from 'asterX-UI/Icons/FileIcon'
import { useContractStore } from 'context/ContractStore'
import { DeleteIcon } from 'asterX-UI/Icons/DeleteIcon'
import { BlueLines } from 'asterX-UI/Icons/BlueLines'
import { getValidDate } from 'lib/utils'
import { useState } from 'react'
import { CollectionService } from 'lib/api/services/collection.service'
import { useSnackbar } from 'notistack'
import { debounce } from 'lodash'
import { NotFound } from 'components/NotFound/NotFound'
import ActionModal from 'asterX-UI/Modals/ActionModal'
import { useResErrors } from 'lib/hooks'
import { Visibility } from '@material-ui/icons'
import { useStyles } from './uploaded-files.styles'

export const UploadedFiles = ({ client = false, creditFlow = false }) => {
  const classes = useStyles()
  const { standardCheck } = useResErrors()
  const { enqueueSnackbar } = useSnackbar()
  const {
    contractStore: { contractAll, documents },
    contractStoreFunc
  } = useContractStore()

  const [openModal, setOpenModal] = useState(false)
  const [urlFile, setUrlFile] = useState(null)

  const reportError = (message = 'Что-то пошло не так', status = 400, path = '') => {
    standardCheck(
      { status, msg: { message }, path, err: true },
      null,
      { textErr: '' }
    )
  }

  const extractMessage = async data => {
    if (!data) return ''
    if (typeof data === 'string') return data
    if (data instanceof Blob) {
      try {
        const text = await data.text()
        try {
          const json = JSON.parse(text)
          return (
            json?.message ||
            json?.error?.message ||
            json?.detail ||
            (Array.isArray(json?.errors) && json.errors[0]?.message) ||
            text
          )
        } catch (e) {
          return text
        }
      } catch (e) {
        return ''
      }
    }
    if (typeof data === 'object') {
      return (
        data?.message ||
        data?.error?.message ||
        data?.detail ||
        (Array.isArray(data?.errors) && data.errors[0]?.message) ||
        ''
      )
    }
    return ''
  }

  const handleDownload = async (e, url) => {
    if (e && e.preventDefault) e.preventDefault()
    if (!url) return
    const a = document.createElement('a')
    a.href = url
    a.target = '_blank'
    a.rel = 'noopener'
    document.body.appendChild(a)
    a.click()
    a.remove()
  }

  const handleOpenModal = async (e, url) => {
    if (e && e.preventDefault) e.preventDefault()
    setUrlFile(url)
    setOpenModal(true)
  }

  const handleDelete = async () => {
    if (!urlFile || !contractAll?.id) {
      reportError('Что-то пошло не так', 400)
      return
    }

    const data = {
      workListId: contractAll?.id,
      downloadUrl: urlFile
    }

    await CollectionService.deleteWlFile(data)
      .then(res =>
        standardCheck(res, () => {
          if (res.data) {
            enqueueSnackbar('Успешно', { variant: 'success' })
            contractStoreFunc.getWlFiles(contractAll?.id)
            setOpenModal(false)
          }
        })
      )
      .finally(() => {})
  }

  const handleDownloadBsFiles = debounce(async item => {
    try {
      if (item?.id && !creditFlow) {
        const res = await CollectionService.getBsFile(item.id)
        if (!res?.data) {
          const msg = (await extractMessage(res?.data)) || `Что-то пошло не так, статус ${res?.status}`
          reportError(msg, res?.status, `getBsFile:${item.id}`)
          return
        }
        const contentType = res.headers['content-type'] || res?.data?.type || ''
        if (contentType.includes('application/json') && res.data instanceof Blob) {
          const msg = (await extractMessage(res.data)) || 'Ошибка'
          reportError(msg, 400, `getBsFile:${item.id}`)
          return
        }
        const disposition = res.headers['content-disposition']
        const blob = new Blob([res.data], { type: contentType })
        let fileName = item?.originalName || 'file'
        if (disposition && disposition.includes('attachment')) {
          const fileNameMatch = disposition.match(/filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/)
          if (fileNameMatch != null && fileNameMatch[1]) {
            fileName = fileNameMatch[1].replace(/['"]/g, '')
          }
        }
        const url = window.URL.createObjectURL(blob)
        const link = document.createElement('a')
        link.href = url
        link.setAttribute('download', fileName)
        document.body.appendChild(link)
        link.click()
        link.remove()
        window.URL.revokeObjectURL(url)
      }
    } catch (error) {
      console.error('Ошибка при загрузке файла', error)
    }
  }, 300)

  const handleOpenBsFiles = debounce(async item => {
    try {
      if (item?.id && !creditFlow) {
        const res = await CollectionService.getBsFile(item.id)
        if (!res?.data) {
          const msg = (await extractMessage(res?.data)) || `Что-то пошло не так, статус ${res?.status}`
          reportError(msg, res?.status, `getBsFile:${item.id}`)
          return
        }
        const contentType = res.headers['content-type'] || res?.data?.type || ''
        if (contentType.includes('application/json') && res.data instanceof Blob) {
          const msg = (await extractMessage(res.data)) || 'Ошибка'
          reportError(msg, 400, `getBsFile:${item.id}`)
          return
        }
        const blob = new Blob([res.data], { type: contentType })
        const url = window.URL.createObjectURL(blob)
        window.open(url)
        window.URL.revokeObjectURL(url)
      } else if (item?.downloadUrl) {
        window.open(item.downloadUrl, '_blank')
      }
    } catch (error) {
      const res = error?.response
      if (res?.data instanceof Blob) {
        try {
          const msg = (await extractMessage(res.data)) || 'Ошибка при открытии файла'
          reportError(msg, res?.status || 400)
          return
        } catch (_) {}
      }
      const msg = (await extractMessage(res?.data)) || error?.message || 'Ошибка при открытии файла'
      reportError(msg, res?.status || 400)
    }
  }, 300)

  const openFile = link => {
    if(!link) return null
    const a = document.createElement('a')
    a.href = link
    a.target = '_blank'
    a.rel = 'noopener'
    document.body.appendChild(a)
    a.click()
    a.remove()
  }

  const groupAndSortFiles = documents => {
    const filteredFiles = documents?.sort((a, b) => b?.id - a?.id)
    const groupedFiles = filteredFiles?.reduce((acc, file) => {
      if (!acc[file.tagName]) {
        acc[file.tagName] = []
      }
      acc[file.tagName].push({
        id: file.id,
        originalName: file.originalName,
        createdDate: file.createdDate,
        downloadUrl: file.downloadUrl
      })
      return acc
    }, {})

    return groupedFiles
  }

  const filteredCollectionFiles = documents?.collectionFiles?.sort(
    (a, b) => new Date(b?.createdDate) - new Date(a?.createdDate)
  )
  const groupedBsFiles = groupAndSortFiles(documents?.bsFiles)
  const groupedClientFiles = groupAndSortFiles(documents?.clientFiles)
  const groupedCreditFlowFiles = groupAndSortFiles(documents?.creditFlowFiles)

  if (!client && !creditFlow && documents?.bsFiles?.length === 0 && documents?.collectionFiles?.length === 0) {
    return <NotFound title="Нет файлов" />
  }

  if (client && documents?.clientFiles?.length === 0) {
    return <NotFound title="Нет файлов" />
  }
  if (creditFlow && documents?.creditFlowFiles?.length === 0) {
    return <NotFound title="Нет файлов" />
  }

  return (
    <Grid container xs={12} className={classes.wrapper}>
      {!client &&
        !creditFlow &&
        (filteredCollectionFiles || []).map(item => (
          <Grid item xs={12} className={classes.collectionFilesContainer} key={`key-${item?.id}`}>
            <Grid item className={classes.fileName}>
              {item?.type?.name}
            </Grid>
            <Grid item xs={12} md={5} className={classes.fileWrapper}>
              <Grid item className={classes.fileContainer}>
                <Grid item container alignItems={'flex-start'} style={{ flexWrap: 'nowrap' }} spacing={3}>
                  <Grid item className={classes.fileIcon}>
                    <FileIcon />
                    <Box className={classes.blueLines}>
                      <BlueLines />
                    </Box>
                  </Grid>
                  <Grid item>
                    <Typography variant={'body2'} className={classes.label}>
                      {item?.type?.name}
                    </Typography>
                    <Typography variant={'body2'} className={classes.date}>
                      {getValidDate(item?.createdDate, true)}
                    </Typography>
                  </Grid>
                </Grid>

                <Grid item className={classes.iconWrapper}>
                  <IconButton className={classes.iconBtn} onClick={e => handleDownload(e, item?.downloadUrl)}>
                    <DownloadIcon className={classes.actionBtn} />
                  </IconButton>
                  <IconButton className={classes.iconBtn} onClick={e => handleOpenModal(e, item?.downloadUrl)}>
                    <DeleteIcon className={classes.actionBtn} />
                  </IconButton>
                </Grid>
              </Grid>
            </Grid>
          </Grid>
        ))}
      {!client &&
        !creditFlow &&
        Object.entries(groupedBsFiles || []).map(([tagName, value]) => (
          <Grid item container className={classes.documentContainer} key={`key-${tagName}`} spacing={2}>
            <Grid item className={classes.fileName}>
              {tagName}
            </Grid>
            <Grid item xs={12} md={5} className={classes.fileWrapper}>
              {(value || []).map(item => (
                <Grid item className={classes.fileContainer} key={`key-${item?.id}`}>
                  <Grid item container alignItems={'flex-start'} style={{ flexWrap: 'nowrap' }} spacing={3}>
                    <Grid item className={classes.fileIcon}>
                      <FileIcon />
                      <Box className={classes.blueLines}>
                        <BlueLines />
                      </Box>
                    </Grid>
                    <Grid item>
                      <Typography variant={'body2'} className={classes.label}>
                        {item?.originalName}
                      </Typography>
                      <Typography variant={'body2'} className={classes.date}>
                        {getValidDate(item?.createdDate, true)}
                      </Typography>
                    </Grid>
                  </Grid>

                  <Grid item className={classes.iconWrapper}>
                    <IconButton className={classes.iconBtn} onClick={e => handleOpenBsFiles(item)}>
                      <Visibility className={classes.actionBtn} />
                    </IconButton>
                    <IconButton className={classes.iconBtn} onClick={() => handleDownloadBsFiles(item)}>
                      <DownloadIcon className={classes.actionBtn} />
                    </IconButton>
                  </Grid>
                </Grid>
              ))}
            </Grid>
          </Grid>
        ))}

      {client &&
        Object.entries(groupedClientFiles || []).map(([tagName, value]) => (
          <Grid item container className={classes.documentContainer} key={`key-${tagName}`} spacing={2}>
            <Grid item className={classes.fileName}>
              {tagName}
            </Grid>
            <Grid item xs={12} md={5} className={classes.fileWrapper}>
              {(value || []).map(item => (
                <Grid item className={classes.fileContainer} key={`key-${item?.id}`}>
                  <Grid item container alignItems={'flex-start'} style={{ flexWrap: 'nowrap' }} spacing={3}>
                    <Grid item className={classes.fileIcon}>
                      <FileIcon />
                      <Box className={classes.blueLines}>
                        <BlueLines />
                      </Box>
                    </Grid>
                    <Grid item>
                      <Typography variant={'body2'} className={classes.label}>
                        {item?.originalName}
                      </Typography>
                      <Typography variant={'body2'} className={classes.date}>
                        {getValidDate(item?.createdDate, true)}
                      </Typography>
                    </Grid>
                  </Grid>

                  <Grid item className={classes.iconWrapper}>
                    <IconButton
                      className={classes.iconBtn}
                      onClick={e => handleOpenBsFiles(item)}
                      style={{ display: item?.originalName?.toLowerCase()?.endsWith('.pdf') ? 'block' : 'none' }}
                    >
                      <Visibility className={classes.actionBtn} />
                    </IconButton>
                    <IconButton className={classes.iconBtn} onClick={() => handleDownloadBsFiles(item)}>
                      <DownloadIcon className={classes.actionBtn} />
                    </IconButton>
                  </Grid>
                </Grid>
              ))}
            </Grid>
          </Grid>
        ))}
      {creditFlow &&
        Object.entries(groupedCreditFlowFiles || []).map(([tagName, value]) => (
          <Grid item container className={classes.documentContainer} key={`key-${tagName}`} spacing={2}>
            <Grid item className={classes.fileName}>
              {tagName}
            </Grid>
            <Grid item xs={12} md={5} className={classes.fileWrapper}>
              {(value || []).map(item => (
                <Grid
                  item
                  className={classes.fileContainer}
                  key={`key-${item?.id || item?.downloadUrl || item?.originalName}`}
                >
                  <Grid item container alignItems={'flex-start'} style={{ flexWrap: 'nowrap' }} spacing={3}>
                    <Grid item className={classes.fileIcon}>
                      <FileIcon />
                      <Box className={classes.blueLines}>
                        <BlueLines />
                      </Box>
                    </Grid>
                    <Grid item>
                      <Typography variant={'body2'} className={classes.label}>
                        {item?.originalName}
                      </Typography>
                      <Typography variant={'body2'} className={classes.date}>
                        {getValidDate(item?.createdDate, true)}
                      </Typography>
                    </Grid>
                  </Grid>
                  <Grid item className={classes.iconWrapper}>
                    {/*<IconButton className={classes.iconBtn} onClick={() => handleOpenBsFiles(item)}>*/}
                    {/*  <Visibility className={classes.actionBtn} />*/}
                    {/*</IconButton>*/}
                    <IconButton className={classes.iconBtn} onClick={() => openFile(item?.downloadUrl)}>
                      <DownloadIcon className={classes.actionBtn} />
                    </IconButton>
                  </Grid>
                </Grid>
              ))}
            </Grid>
          </Grid>
        ))}
      <ActionModal
        open={openModal}
        onClose={() => setOpenModal(false)}
        onCancel={() => setOpenModal(false)}
        onConfirm={() => handleDelete()}
        title="Вы действительно хотите удалить этот файл?"
        labelConfirm="Да"
        labelCancel="Нет"
      />
    </Grid>
  )
}
